import { Button } from "../Control/Button/Button";
import EmailInput from "../Control/InputBox/EmailInput";
import PasswordInput from "../Control/InputBox/PasswordInput";
import { GoogleAuthProvider, signOut, signInWithPopup, signInWithEmailAndPassword, AuthError } from 'firebase/auth';
import { auth } from '../../firebase';
import { useEffect, useState } from "react";
import Signup from "./Signup";
import Toggle from "../Toggle/Toggle";
import { ForgotPassword } from "./ForgotPassword";
import { addTimedNotification, showFailure } from "../NotificationStack/notificationSlice";
import { isEmail } from "../../models/Validators";

export type EmailPasswordLogin = {
    email: string | null,
    password: string | null,
    isEmailValid: boolean,
    isPasswordValid: boolean,
    rememberMe: boolean
}

export type LoginView = "login" | "signup" | "forgot-password";

const REMEMBER_ME_STORAGE_KEY = 'materiahunter-remember-email';

export type LoginSignupProps = {
    onLogin?: () => void
}

export default function LoginSignup(props: LoginSignupProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState<EmailPasswordLogin>({
        email: "",
        password: "",
        isEmailValid: false,
        isPasswordValid: false,
        rememberMe: false
    });

    const [loginView, setLoginView] = useState<LoginView>("login");

    const googleLogin = () => {
        const provider = new GoogleAuthProvider();
        signInWithPopup(auth, provider).then(result => {
            if (props.onLogin) {
                props.onLogin();
            }
        }).catch(error => {
            console.error(error);
            console.error("fail");
        });
    };

    const emailPasswordLogin = () => {
        if (!state.email || !state.password) {
            return;
        }

        if (state.rememberMe) {
            localStorage.setItem(REMEMBER_ME_STORAGE_KEY, state.email);
        } else {
            localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
        }

        setIsLoading(true);
        signInWithEmailAndPassword(auth, state.email, state.password)
            .then((userCredential) => {
                if (props.onLogin) {
                    props.onLogin();
                }
            })
            .catch((error: AuthError) => {
                let errorMessage = "";

                switch (error.code) {
                    case "auth/user-not-found":
                        errorMessage = "This user doesn't exist.";
                        break;
                    case "auth/wrong-password":
                        errorMessage = "The password entered was incorrect."
                        break;
                    case "auth/invalid-email":
                        errorMessage = "Invalid email address."
                        break;
                    default:
                        errorMessage = "Could not sign in!"
                        break;
                }

                showFailure(errorMessage);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const update = (updateFunc: (stateCopy: EmailPasswordLogin) => void) => {
        const stateCopy: EmailPasswordLogin = JSON.parse(JSON.stringify(state));
        updateFunc(stateCopy);
        setState(stateCopy);
    }

    useEffect(() => {
        const storedEmail = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
        if (!storedEmail) {
            return;
        }

        update((copy) => {
            copy.rememberMe = true;
            copy.email = storedEmail;
            copy.isEmailValid = isEmail(storedEmail)
        });
    }, []);

    if (loginView === "login") {
        return (
            <>
                <div className="flex min-h-full flex-1 flex-col justify-center">
                    <div className="sm:mx-auto sm:w-full sm:max-w-md">
                        <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-contrast-primary">
                            Sign in to your account
                        </h2>
                    </div>

                    <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-[480px]">
                        <div className="bg-secondary px-6 py-12 shadow sm:rounded-lg">
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-contrast-primary">
                                        Email address
                                    </label>
                                    <div className="mt-2">
                                        <EmailInput
                                            required
                                            onChange={(value, hasValidationErrors) => update((copy) => {
                                                copy.email = value;
                                                copy.isEmailValid = !hasValidationErrors;
                                            })}
                                            value={state.email} />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium leading-6 text-contrast-primary">
                                        Password
                                    </label>
                                    <div className="mt-2">
                                        <PasswordInput
                                            required
                                            onChange={(value, hasValidationErrors) => update((copy) => {
                                                copy.password = value;
                                                copy.isPasswordValid = !hasValidationErrors;
                                            })}
                                            value={state.password} />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between flex-wrap gap-y-2">
                                    <div className="flex items-center">
                                        <Toggle
                                            isEnabled={state.rememberMe}
                                            label="Remember me"
                                            onToggleChange={(isEnabled) => update((copy) => copy.rememberMe = isEnabled)} />
                                    </div>

                                    <div className="text-sm leading-6">
                                        <button
                                            type="button"
                                            className="font-semibold leading-6 text-emphasis hover:text-emphasis-secondary"
                                            onClick={() => {
                                                window.scrollTo({ top: 0 });
                                                setLoginView("forgot-password");
                                            }}
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <Button
                                        color="emphasis"
                                        onClick={() => emailPasswordLogin()}
                                        className="w-full"
                                        disabled={isLoading}
                                    >
                                        Sign in
                                    </Button>
                                </div>
                            </div>

                            <div>
                                <div className="relative mt-10">
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div className="w-full border-t border-color-border" />
                                    </div>
                                    <div className="relative flex justify-center text-sm font-medium leading-6">
                                        <span className="bg-primary px-6 text-contrast-primary">Or continue with</span>
                                    </div>
                                </div>

                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <Button color="component" onClick={googleLogin} className="flex items-center gap-3 px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset justify-center">
                                        <svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
                                            <path
                                                d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                                                fill="#EA4335"
                                            />
                                            <path
                                                d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                                                fill="#4285F4"
                                            />
                                            <path
                                                d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                                                fill="#FBBC05"
                                            />
                                            <path
                                                d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                                                fill="#34A853"
                                            />
                                        </svg>
                                        <span className="text-sm font-semibold leading-6">Google</span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <p className="mt-10 text-center text-sm text-gray-500">
                            Not a member?{' '}
                            <button
                                type="button"
                                className="font-semibold leading-6 text-emphasis hover:text-emphasis-secondary"
                                onClick={() => {
                                    window.scrollTo({ top: 0 });
                                    setLoginView("signup");
                                }}
                            >
                                Sign up!
                            </button>
                        </p>
                    </div>
                </div>
            </>
        )
    }

    if (loginView === "signup") {
        return (
            <>
                <Signup />
            </>
        )
    }

    if (loginView === "forgot-password") {
        return (
            <>
                <ForgotPassword />
            </>
        )
    }

    return (
        <></>
    )
}
